import React, { useState, useEffect, useMemo } from 'react';
import { SurveyData, DynamicQuestion, SurveySection } from '../types';
import { getSurveys, getDynamicQuestions, getDynamicSections } from '../services/storage';
import { Search, Filter, Loader, User as UserIcon, Eye, X, Home } from 'lucide-react';

const AdvancedSearch: React.FC = () => {
  const [data, setData] = useState<SurveyData[]>([]);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [searchMode, setSearchMode] = useState<'households' | 'individuals'>('households');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterValue, setFilterValue] = useState('');
  
  const [selectedIndividual, setSelectedIndividual] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [surveysData, qs, secs] = await Promise.all([getSurveys(), getDynamicQuestions(), getDynamicSections()]);
        setData(surveysData);
        setQuestions(qs);
        setSections(secs);
      } catch (error) {
        console.error("Failed to load search data", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getFilterableQuestions = () => {
    // If searching individuals, only show filters for individual questions
    if (searchMode === 'individuals') {
      const repeaterSec = sections.find(s => s.isRepeater);
      if (repeaterSec) {
        return questions.filter(q => q.section === repeaterSec.id && ['text', 'dropdown', 'multiselect', 'number', 'yesno'].includes(q.type));
      }
    }
    return questions.filter(q => ['text', 'dropdown', 'multiselect', 'number', 'yesno'].includes(q.type));
  };

  const allIndividuals = useMemo(() => {
    const repeaterSec = sections.find(s => s.isRepeater);
    if (!repeaterSec) return [];
    const inds: any[] = [];
    data.forEach(survey => {
      const members = survey[repeaterSec.id] || [];
      members.forEach((m: any) => {
        inds.push({
          ...m,
          _parentSurveyId: survey.id,
          _houseName: survey.sectionA?.houseName || '',
          _headName: survey.sectionA?.headName || '',
          _phone: survey.sectionA?.phone || survey.phone || '',
          _wardNo: survey.sectionA?.wardNo || '',
          _houseNo: survey.sectionA?.houseNo || ''
        });
      });
    });
    return inds;
  }, [data, sections]);

  const filteredHouseholds = data.filter(survey => {
    let matchesSearch = false;
    let matchesFilter = true;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const checkObject = (obj: any): boolean => {
        if (!obj) return false;
        if (typeof obj === 'string') return obj.toLowerCase().includes(term);
        if (Array.isArray(obj)) return obj.some(checkObject);
        if (typeof obj === 'object') return Object.values(obj).some(checkObject);
        return false;
      };
      matchesSearch = sections.some(sec => checkObject(survey[sec.id]));
    } else {
      matchesSearch = true;
    }

    if (filterCategory && filterValue) {
      const q = questions.find(q => q.id === filterCategory);
      if (q) {
        const sec = sections.find(s => s.id === q.section);
        if (sec) {
          if (sec.isRepeater) {
            const items = survey[sec.id] || [];
            matchesFilter = items.some((item: any) => {
              const val = item[q.id];
              return val && String(val).toLowerCase().includes(filterValue.toLowerCase());
            });
          } else {
            const val = survey[sec.id]?.[q.id];
            matchesFilter = val && String(val).toLowerCase().includes(filterValue.toLowerCase());
          }
        }
      }
    }

    return matchesSearch && matchesFilter;
  });

  const filteredIndividuals = allIndividuals.filter(ind => {
    let matchesSearch = false;
    let matchesFilter = true;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const checkObject = (obj: any): boolean => {
        if (!obj) return false;
        if (typeof obj === 'string') return obj.toLowerCase().includes(term);
        if (Array.isArray(obj)) return obj.some(checkObject);
        if (typeof obj === 'object') return Object.values(obj).some(checkObject);
        return false;
      };
      matchesSearch = checkObject(ind);
    } else {
      matchesSearch = true;
    }

    if (filterCategory && filterValue) {
      const val = ind[filterCategory];
      matchesFilter = val && String(val).toLowerCase().includes(filterValue.toLowerCase());
    }

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader className="animate-spin text-emerald-600" size={32} /></div>;
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Advanced Search</h2>
      </div>

      <div className="flex space-x-6 border-b border-gray-200">
        <button 
          className={`pb-3 px-2 font-bold flex items-center space-x-2 transition-colors ${searchMode === 'households' ? 'border-b-2 border-emerald-600 text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`} 
          onClick={() => {
            setSearchMode('households');
            setFilterCategory('');
            setFilterValue('');
          }}
        >
          <Home size={18} />
          <span>Households</span>
        </button>
        <button 
          className={`pb-3 px-2 font-bold flex items-center space-x-2 transition-colors ${searchMode === 'individuals' ? 'border-b-2 border-emerald-600 text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`} 
          onClick={() => {
            setSearchMode('individuals');
            setFilterCategory('');
            setFilterValue('');
          }}
        >
          <UserIcon size={18} />
          <span>Individuals</span>
        </button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder={searchMode === 'households' ? "Search households, names, phones..." : "Search individual names, details..."}
              className="pl-10 p-2 border rounded w-full outline-none focus:ring-2 focus:ring-emerald-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 flex-1">
            <div className="relative flex-1">
              <Filter className="absolute left-3 top-2.5 text-gray-400" size={18} />
              <select 
                className="pl-10 p-2 border rounded w-full outline-none focus:ring-2 focus:ring-emerald-500 appearance-none"
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setFilterValue('');
                }}
              >
                <option value="">Select Filter Category...</option>
                {getFilterableQuestions().map(q => (
                  <option key={q.id} value={q.id}>{q.label}</option>
                ))}
              </select>
            </div>
            
            {filterCategory && (
              <input 
                type="text" 
                placeholder="Filter value..." 
                className="p-2 border rounded flex-1 outline-none focus:ring-2 focus:ring-emerald-500"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {searchMode === 'households' ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sl No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Head Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">House Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Members Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Family Members</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredHouseholds.map((survey) => {
                const repeaterSec = sections.find(s => s.isRepeater);
                const members = repeaterSec && survey[repeaterSec.id] ? survey[repeaterSec.id] : [];
                const membersCount = members.length;
                
                return (
                  <tr key={survey.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{survey.sectionA?.serialNo || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{survey.sectionA?.headName || <span className="text-gray-400 italic">(No Name)</span>}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col">
                        <span>{survey.sectionA?.houseName || '-'}</span>
                        <span className="text-xs text-gray-400">Ward: {survey.sectionA?.wardNo}, No: {survey.sectionA?.houseNo}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-mono">
                      {survey.sectionA?.phone || survey.phone || <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <UserIcon size={14} className="text-emerald-500" />
                        <span>{membersCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex flex-wrap gap-1">
                        {members.map((m: any, idx: number) => {
                          const name = m.name || 'Unknown';
                          const isMatch = searchTerm && name.toLowerCase().includes(searchTerm.toLowerCase());
                          return (
                            <span key={idx} className={`px-2 py-1 rounded-full text-xs ${isMatch ? 'bg-emerald-100 text-emerald-800 font-bold border border-emerald-300' : 'bg-gray-100 text-gray-600'}`}>
                              {name}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )
              })}
              {filteredHouseholds.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500 italic">No households found matching your search.</td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age / Gender</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Relation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">House Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredIndividuals.map((ind, idx) => {
                const isMatch = searchTerm && ind.name?.toLowerCase().includes(searchTerm.toLowerCase());
                return (
                  <tr key={`${ind._parentSurveyId}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <span className={isMatch ? 'bg-emerald-100 text-emerald-800 px-1 rounded' : ''}>{ind.name || 'Unknown'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {ind.age || '-'} / {ind.gender || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {ind.relation || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {ind._houseName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-mono">
                      {ind._phone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button 
                        onClick={() => setSelectedIndividual(ind)}
                        className="text-emerald-600 hover:text-emerald-800 flex items-center space-x-1 font-medium bg-emerald-50 px-3 py-1 rounded-lg transition-colors"
                      >
                        <Eye size={16} />
                        <span>View Data</span>
                      </button>
                    </td>
                  </tr>
                )
              })}
              {filteredIndividuals.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500 italic">No individuals found matching your search.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Individual Details Modal */}
      {selectedIndividual && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 border-b flex justify-between items-center bg-emerald-50 rounded-t-xl">
              <h3 className="text-xl font-bold text-emerald-800 flex items-center">
                <UserIcon className="mr-2" size={24} />
                {selectedIndividual.name || 'Member Details'}
              </h3>
              <button 
                onClick={() => setSelectedIndividual(null)}
                className="p-1 hover:bg-emerald-200 rounded-full transition-colors"
              >
                <X size={24} className="text-emerald-700"/>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">House Name</p>
                  <p className="font-bold text-gray-800">{selectedIndividual._houseName || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Head of Family</p>
                  <p className="font-bold text-gray-800">{selectedIndividual._headName || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Phone</p>
                  <p className="font-bold text-gray-800">{selectedIndividual._phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Ward / House No</p>
                  <p className="font-bold text-gray-800">{selectedIndividual._wardNo || '-'} / {selectedIndividual._houseNo || '-'}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-bold text-gray-700 border-b border-gray-200 pb-2 mb-4 text-lg">Personal Data</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                  {questions
                    .filter(q => q.section === sections.find(s => s.isRepeater)?.id)
                    .map(q => {
                      const val = selectedIndividual[q.id];
                      if (val === undefined || val === null || val === '') return null;
                      
                      let displayVal = String(val);
                      if (typeof val === 'boolean') displayVal = val ? 'Yes' : 'No';
                      if (Array.isArray(val)) displayVal = val.join(', ');
                      
                      return (
                        <div key={q.id} className="bg-white p-3 rounded border border-gray-100 shadow-sm">
                          <p className="text-xs text-gray-500 mb-1">{q.label}</p>
                          <p className="font-medium text-gray-900 break-words">{displayVal}</p>
                        </div>
                      )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
